import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hexagon, Eye, EyeOff, ArrowLeft, Crown, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState('fan');
  const [creatorName, setCreatorName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!agreed) { 
      setError('Please accept the terms'); 
      return; 
    }

    if (role === 'creator' && !creatorName.trim()) {
      setError('Creator name is required');
      return;
    }
    
    setError('');
    setLoading(true);
    try {
      await signup(name, email, password, {
        is_creator: role === 'creator',
        creator_name: role === 'creator' ? creatorName.trim() : ''
      });
      navigate('/verify-email');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const pwStrength = password.length >= 8 ? (password.length >= 12 ? 'Strong' : 'Good') : password.length > 0 ? 'Weak' : '';
  const pwColor = pwStrength === 'Strong' ? 'text-emerald-400' : pwStrength === 'Good' ? 'text-amber-400' : 'text-red-400';
  
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-6 relative">
      <div className="absolute inset-0 honeycomb-bg opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-sm"
      >
        <button
          data-testid="signup-back-btn"
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="relative">
            <Hexagon className="w-10 h-10 text-amber-500 fill-amber-500/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-amber-500 rounded-full" />
            </div>
          </div>
          <div>
            <h1 className="font-heading font-black text-2xl text-white">Join Honey Pot</h1>
            <p className="text-sm text-zinc-500">Protect creators while you browse</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div data-testid="signup-error" className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Role Toggle */}
          <div className="flex gap-2" data-testid="signup-role-toggle">
            <button
              type="button"
              onClick={() => setRole('fan')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all border ${
                role === 'fan'
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                  : 'bg-white/[0.02] border-white/5 text-zinc-500 hover:border-white/10'
              }`}
              data-testid="signup-role-fan"
            >
              <ShieldCheck className="w-4 h-4" />
              I'm a Fan
            </button>
            <button
              type="button"
              onClick={() => setRole('creator')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all border ${
                role === 'creator'
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                  : 'bg-white/[0.02] border-white/5 text-zinc-500 hover:border-white/10'
              }`}
              data-testid="signup-role-creator"
            >
              <Crown className="w-4 h-4" />
              I'm a Creator
            </button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm text-zinc-400">Full Name</Label>
            <Input
              id="name"
              data-testid="signup-name-input"
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20"
              required
            />
          </div>

          {/* Creator Name Field */}
          <AnimatePresence>
            {role === 'creator' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden space-y-2"
              >
                <Label htmlFor="creator-name" className="text-sm text-zinc-400">Creator Name</Label>
                <Input
                  id="creator-name"
                  data-testid="signup-creator-name-input"
                  type="text"
                  placeholder="e.g. TechReviewPro"
                  value={creatorName}
                  onChange={e => setCreatorName(e.target.value)}
                  className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20"
                  required={role === 'creator'}
                />
                <p className="text-[10px] text-zinc-600">This is how your supporters will find you</p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-2">
            <Label htmlFor="s-email" className="text-sm text-zinc-400">Email</Label>
            <Input
              id="s-email"
              data-testid="signup-email-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 focus:border-amber-500/50 focus:ring-amber-500/20"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="s-password" className="text-sm text-zinc-400">Password</Label>
            <div className="relative">
              <Input
                id="s-password"
                data-testid="signup-password-input"
                type={showPw ? 'text' : 'password'}
                placeholder="Min 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 pr-10 focus:border-amber-500/50 focus:ring-amber-500/20"
                required
                minLength={6}
              />
              <button
                type="button"
                data-testid="signup-toggle-pw"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {pwStrength && (
              <p className={`text-xs ${pwColor}`}>Password strength: {pwStrength}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="s-confirm-password" className="text-sm text-zinc-400">Confirm Password</Label>
            <div className="relative">
              <Input
                id="s-confirm-password"
                data-testid="signup-confirm-password-input"
                type={showConfirmPw ? 'text' : 'password'}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className={`bg-[#121214] border-white/10 text-white placeholder:text-zinc-600 h-11 pr-10 focus:border-amber-500/50 focus:ring-amber-500/20 ${
                  passwordsMismatch ? 'border-red-500/50' : passwordsMatch ? 'border-emerald-500/50' : ''
                }`}
                required
                minLength={6}
              />
              <button
                type="button"
                data-testid="signup-toggle-confirm-pw"
                onClick={() => setShowConfirmPw(!showConfirmPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
              >
                {showConfirmPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {passwordsMatch && (
              <p className="text-xs text-emerald-400">Passwords match ✓</p>
            )}
            {passwordsMismatch && (
              <p className="text-xs text-red-400">Passwords do not match</p>
            )}
          </div>

          <div className="flex items-start gap-2 pt-1">
            <Checkbox
              id="terms"
              data-testid="signup-terms-checkbox"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 mt-0.5"
            />
            <label htmlFor="terms" className="text-xs text-zinc-500 leading-relaxed cursor-pointer">
              I agree to the Terms of Service and Privacy Policy
            </label>
          </div>

          <button
            type="submit"
            data-testid="signup-submit-btn"
            disabled={loading || passwordsMismatch}
            className="honey-btn w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-zinc-500 mt-6">
          Already have an account?{' '}
          <button
            data-testid="signup-to-login"
            onClick={() => navigate('/login')}
            className="text-amber-500 hover:text-amber-400 font-medium"
          >
            Sign in
          </button>
        </p>
      </motion.div>
    </div>
  );
}
